import { refundMethods as REFUND_METHODS } from '@reveni-io/reveni-ui'

import { availableLanguagesForUser, issueRefundAtStatusValues } from '@/common/constants'
import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getEmptyTranslations } from '@/configuration/pages/ConfigurationPage/helpers'

import { extraFeeTypes } from './RefundMethod/constants'
import {
  getDurationInDays,
  getRefundMethodPayload,
  isValidityAndExtraCreditMethod,
} from './RefundMethod/RefundMethodContent/helpers'
import { refundMethodErrorFields, refundMethodFieldsOrder, refundMethodMessageFields } from './constants'

export const getPreferencesPayload = issueRefundAtStatus => {
  return {
    issue_refund_at_status: issueRefundAtStatus === issueRefundAtStatusValues.manual ? null : issueRefundAtStatus,
  }
}

export const getDisplayErrorField = language => `display.${language}`

const isEmptyAmount = amount => amount === '' || amount === null

export const getRefundMethodErrors = (refundMethod, languages) => {
  const errors = {}

  if (refundMethod.useCustomName) {
    languages.forEach(language => {
      if (!refundMethod.display[language]?.trim()) errors[getDisplayErrorField(language)] = true
    })
  }

  if (!isValidityAndExtraCreditMethod(refundMethod)) return errors

  const expires = refundMethod.name === REFUND_METHODS.storeCredit || refundMethod.duration.number !== null

  if (expires && !(Number(refundMethod.duration.number) > 0 && refundMethod.duration.type)) {
    errors[refundMethodErrorFields.duration] = true
  }

  const needsExtraValue = [extraFeeTypes.fixedAmount, extraFeeTypes.percentage].includes(refundMethod.extra_value_type)

  if (needsExtraValue && isEmptyAmount(refundMethod.extra_value)) errors[refundMethodErrorFields.extraValue] = true

  return errors
}

export const getRefundMethodsErrors = (refundMethods, languages) => {
  const errors = {}

  refundMethods.forEach(refundMethod => {
    const refundMethodErrors = getRefundMethodErrors(refundMethod, languages)

    if (Object.keys(refundMethodErrors).length > 0) errors[refundMethod.name] = refundMethodErrors
  })

  return errors
}

export const getRefundMethodsErrorsAfterChange = (errors, refundMethod, languages) => {
  const { [refundMethod.name]: previousErrors, ...otherErrors } = errors

  if (!previousErrors) return errors

  const refundMethodErrors = getRefundMethodErrors(refundMethod, languages)

  if (Object.keys(refundMethodErrors).length === 0) return otherErrors

  return { ...otherErrors, [refundMethod.name]: refundMethodErrors }
}

export const countRefundMethodErrors = refundMethodErrors => Object.keys(refundMethodErrors).length

export const getRefundMethodFieldId = (refundMethodName, field) => {
  const fieldKey = field.startsWith('display.') ? 'display' : field

  return `refund-method-${refundMethodName}-${fieldKey}`
}

export const getFirstErrorField = ({ refundMethods, errors, languages }) => {
  const fieldsOrder = [...languages.map(getDisplayErrorField), ...refundMethodFieldsOrder]

  for (const refundMethod of refundMethods) {
    const refundMethodErrors = errors[refundMethod.name]
    const field = refundMethodErrors && fieldsOrder.find(orderedField => refundMethodErrors[orderedField])

    if (field) return { refundMethodId: refundMethod.id, refundMethodName: refundMethod.name, field }
  }

  return null
}

const mapResponseRefundMethodErrors = (refundMethodErrors, languages) => {
  const errors = {}
  let hasUnmappedErrors = false

  Object.entries(refundMethodErrors).forEach(([field, messages]) => {
    if (field === 'display' && !Array.isArray(messages)) {
      Object.keys(messages).forEach(language => {
        if (languages.includes(language)) {
          errors[getDisplayErrorField(language)] = true
        } else {
          hasUnmappedErrors = true
        }
      })
    } else if (refundMethodMessageFields.includes(field)) {
      errors[field] = messages
    } else if (refundMethodFieldsOrder.includes(field)) {
      errors[field] = true
    } else {
      hasUnmappedErrors = true
    }
  })

  return { errors, hasUnmappedErrors }
}

export const getRefundMethodsErrorsFromResponse = (errorData, languages) => {
  const refundMethodsErrors = errorData?.refund_methods

  if (Array.isArray(refundMethodsErrors))
    return { errors: {}, listErrors: refundMethodsErrors, hasUnmappedErrors: false }
  if (!refundMethodsErrors) return { errors: {}, listErrors: [], hasUnmappedErrors: true }

  const errors = {}
  let hasUnmappedErrors = false

  Object.entries(refundMethodsErrors).forEach(([refundMethodName, refundMethodErrors]) => {
    const mapped = mapResponseRefundMethodErrors(refundMethodErrors, languages)

    if (Object.keys(mapped.errors).length > 0) errors[refundMethodName] = mapped.errors
    if (mapped.hasUnmappedErrors) hasUnmappedErrors = true
  })

  return { errors, listErrors: [], hasUnmappedErrors }
}

export const getCustomNameLanguages = ({ configurationMode, selectedStoreLanguages, stores }) => {
  if (configurationMode === configurationTypes.merchant) {
    const storesLanguages = stores.flatMap(store => store.supported_languages)

    return availableLanguagesForUser.filter(language => storesLanguages.includes(language))
  }

  const supportedLanguages = selectedStoreLanguages || ['en']

  return availableLanguagesForUser.filter(language => supportedLanguages.includes(language))
}

const getDecimalString = amount => Number(amount).toFixed(2)

const getOptionalDecimalString = amount => (isEmptyAmount(amount) ? '0.00' : getDecimalString(amount))

const getDurationConfiguration = duration => {
  if (!duration.type || !(Number(duration.number) > 0)) return undefined

  return `${getDurationInDays(duration.type, duration.number)} 00:00:00`
}

const getRefundMethodConfiguration = ({ refundMethod, languages, position }) => {
  const payload = getRefundMethodPayload({
    refundMethod,
    useCustomName: refundMethod.useCustomName,
    customNames: refundMethod.display,
    instantAllowed: refundMethod.instant_allowed,
    duration: refundMethod.duration,
    extraValue: refundMethod.extra_value,
    extraValueType: refundMethod.extra_value_type,
    minEligibleRefundAmount: refundMethod.min_eligible_refund_amount,
    maxExtraAmount: refundMethod.max_extra_amount,
    supportedLanguages: languages,
    discountCombinations: refundMethod.discount_combinations,
    allowExtraWhenPaidWithCredit: refundMethod.allow_extra_when_paid_with_credit,
  })

  const display = refundMethod.useCustomName ? payload.display : getEmptyTranslations(availableLanguagesForUser)

  if (!isValidityAndExtraCreditMethod(refundMethod)) return { ...payload, display, position }

  return {
    ...payload,
    display,
    position,
    duration: getDurationConfiguration(refundMethod.duration),
    extra_value: isEmptyAmount(refundMethod.extra_value) ? undefined : getDecimalString(refundMethod.extra_value),
    min_eligible_refund_amount: getOptionalDecimalString(refundMethod.min_eligible_refund_amount),
    max_extra_amount: getOptionalDecimalString(refundMethod.max_extra_amount),
  }
}

export const getRefundMethodsConfiguration = ({ refundMethods, issueRefundAtStatus, languages }) => {
  return {
    ...getPreferencesPayload(issueRefundAtStatus),
    refund_methods: refundMethods.map((refundMethod, position) => {
      return getRefundMethodConfiguration({ refundMethod, languages, position })
    }),
  }
}
